import React, { useState, useEffect, useContext } from "react";
import SettingTable from "../ManageSettings/SettingTable";
import useApi from "../../../../utils/api-manager/Helper/useApi";
import AuthContext from "../../.././../utils/secure-route/AuthContext";
import { ToastContainer } from "react-toastify";
import { useToastHandler } from "../../../common/ToastHandler";
import useSwal from "../../../common/ReusableComponent/useSwal";
import OwnerDistributionIcon from "../../../../assets/Image/OwnerDistribution.png";
import CloseIcon from "../../../../assets/Image/close.png";
import CustomDateInput from "../../../../context/CustomDateInput";
import useFormattingUtils from "../../../../context/useFormattingUtils";
import ReusableTable from "../../../common/ReusableComponent/reusableTable/ReusableTable";

export default function OwnerDistribution() {
  const { Post, Get, Delete, Put, getAPI, GetPaginatedData } = useApi();
  const { user } = useContext(AuthContext);
  const storeid = user.store;
  const { handleToast } = useToastHandler();
  const { confirmDelete } = useSwal();
  const [loading, setLoading] = useState(false);
  const [distributions, setDistributions] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [currentRecord, setCurrentRecord] = useState(null);
  const [ownerList, setOwnerList] = useState([]);
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [banks, setBanks] = useState([]);
  const { formatDate } = useFormattingUtils();
  const today = new Date().toISOString().split("T")[0];

  const [formData, setFormData] = useState({
    date: today,
    transaction_type: "Owner Distributions",
    owner: "",
    pay_method: "cash",
    bank: "",
    pay_method_status: "pay_now",
    cheque_no: "",
    note: "",
    amount: "",
  });

  const columns = [
    { header: "Date", field: "date" },
    { header: "Owner", field: "owner_name" },
    { header: "Payment Method", field: "pay_method" },
    { header: "Amount", field: "amount" },
    { header: "Bank", field: "bank_name" },
    { header: "Cheque No", field: "cheque_no" },
  ];

  useEffect(() => {
    fetchOwnerDistributions();
    fetchOwnerList();
    fetchBanks();
  }, []);

  const convertDateToApiFormat = (dateString) => {
    if (!dateString) return null;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }

    return null;
  };

  const convertToInputFormat = (dateString) => {
    if (!dateString) return today;

    if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
      return dateString;
    }

    if (/^\d{2}-\d{2}-\d{4}$/.test(dateString)) {
      const [month, day, year] = dateString.split('-');
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return date.toISOString().split('T')[0];
    }

    return today;
  };

  const fetchOwnerDistributions = async ({ page = 1, searchTerm = "", sort="" } = {}) => {
    try {
      setLoading(true);

       const response = await GetPaginatedData("transactionData", {
      page,
      search: searchTerm,  
      queryParams: {
        ...(sort && { sort }),
          transaction_type: "Owner Distributions"
      }
    });

      const results = response?.data || [];
      const count = response?.totalCount || 0;
      const totalPages = Math.ceil(count / 10);

      const formatted = (results || []).map((item) => ({
        ...item,
        date: item && item.date ? formatDate(item.date) : "N/A",
      }));

      setDistributions(formatted);
      console.log(formatted);
      return { data: formatted, totalPages };
    } catch (error) {
      console.error("Error fetching owner distributions:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchOwnerList = async () => {
    try {
      const response = await Get("user");

      if (response && Array.isArray(response.results)) {
        const storeOwners = response.results.filter(
          (owner) => owner.ms_role_name === "store owner"
        );
        setOwnerList(storeOwners);
      } else {
        console.error("Invalid response structure:", response);
        handleToast({
          type: "ERROR",
          message: "Failed to fetch owner list"
        });
      }
    } catch (err) {
      console.error("Error fetching owner list:", err);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch owner list"
      });
    }
  };

  const fetchBanks = async () => {
    try {
      const response = await Get("bank");
      const bankList = response?.results || [];
      setBanks(bankList);
    } catch (error) {
      console.error("Error fetching banks:", error);
      handleToast({
        type: "ERROR",
        message: "Failed to fetch banks.",
      });
    }
  };

  const resetFormData = () => {
    setFormData({
      date: today,
      transaction_type: "Owner Distributions",
      owner: "",
      pay_method: "cash",
      bank: "",
      pay_method_status: "pay_now",
      cheque_no: "",
      note: "",
      amount: "",
    });
    setIsEditing(false);
    setCurrentRecord(null);
  };

  const toggleModal = () => {
    if (showModal) {
      resetFormData();
    }
    setShowModal(!showModal);

    if (!showModal) {
      setTimeout(() => {
        document.activeElement.blur();
      }, 0);
    }
  };

  //   const handleChange = (e) => {
  //   const { name, value } = e.target;
  //   if (name === "cheque_no" && !/^[0-9]*$/.test(value)) {
  //     return;
  //   }
  //   setFormData((prevData) => ({
  //     ...prevData,
  //     [name]: value,
  //   }));
  // };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevState) => {
      if (name === "cheque_no" && !/^[0-9]*$/.test(value)) {
        return prevState; // Don't update, but don't return undefined!
      }
      const updatedFormData = {
        ...prevState,
        [name]: value,
      };
      if (name === "pay_method") {
        console.log("Pay method updated to:", updatedFormData.pay_method);
      }
      return updatedFormData;
    });
  };

  const handleDateChange = (e) => {
    const { value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      date: value,
    }));
  };

  const handleOwnerDistribution = async (e) => {
    e.preventDefault();

    if (
      !formData.date ||
      !formData.owner ||
      !formData.pay_method ||
      !formData.amount
    ) {
      handleToast({
        type: "ERROR",
        message: "Please fill in all required fields",
      });
      return;
    }

    let selectedOwner = ownerList.find(
      (owner) =>
        `${owner.first_name} ${owner.last_name}`.trim().toLowerCase() ===
        formData.owner.trim().toLowerCase()
    );

    if (!selectedOwner) {
      selectedOwner = ownerList.find(
        (owner) =>
          owner.first_name.trim().toLowerCase() ===
          formData.owner.trim().toLowerCase()
      );
    }

    const payload = {
      ...formData,
      transaction_type: "Owner Distributions",
      store: storeid,
      owner_name: formData.owner,
      owner: selectedOwner ? selectedOwner.id : null,
      pay_method_status: null,
      date: convertDateToApiFormat(formData.date)
    };

    if (!payload.date) {
      handleToast({
        type: "ERROR",
        message: "Invalid date format. Please check the date."
      });
      return;
    }

    Object.keys(payload).forEach((key) => {
      if (
        (payload[key] === undefined || payload[key] === null) &&
        key !== "pay_method_status"
      ) {
        delete payload[key];
      }
    });

    try {
      let response;

      if (isEditing && currentRecord?.id) {
        response = await Put(`transactionData`, currentRecord.id, payload);

        if (response && (response.status === 200 || response.status === 204)) {
          handleToast({
            type: "SUCCESS",
            message: "Distribution Updated Successfully!"
          });
          resetFormData();
          setShowModal(false);
          setRefreshTrigger(prev => prev + 1);
        }
      } else {
        response = await Post("transactionData", payload);

        if (response && (response.status === 200 || response.status === 201)) {
          handleToast({
            type: "SUCCESS",
            message: "Distribution Added Successfully!"
          });
          resetFormData();
          setShowModal(false);
          setRefreshTrigger(prev => prev + 1);
        }
      }
    } catch (err) {
  console.error("Error submitting owner distribution:", err);

  // Try to extract and display specific backend validation errors
  if (err.response && err.response.data) {
    const errors = err.response.data;

    // Loop through all keys (like "cheque_no", "amount", etc.)
    for (const key in errors) {
      if (Array.isArray(errors[key])) {
        handleToast({
          type: "ERROR",
          message: errors[key][0], // Show the first error message
        });
        return;
      }
    }
  }

  // Fallback generic error message
  handleToast({
    type: "ERROR",
    message: "Error submitting distribution. Please check the form and try again.",
  });
}

  };

  const handleEditDistribution = (recordOrId) => {
    console.log("Editing Record:", recordOrId);

    let record;

    if (typeof recordOrId === 'string') {

      record = distributions?.find(item => item.id === recordOrId);
      if (!record) {
        console.error("Record not found for ID:", recordOrId);
        handleToast({
          type: "ERROR",
          message: "Error: Record not found.",
        });
        return;
      }
    } else if (recordOrId && typeof recordOrId === 'object' && recordOrId.id) {

      record = recordOrId;
    } else {
      console.error("Invalid record: Missing ID", recordOrId);
      handleToast({
        type: "ERROR",
        message: "Error: Cannot edit record, missing ID.",
      });
      return;
    }

    const ownerName = record.owner_name || "";

    setFormData({
      date: convertToInputFormat(record.date),
      transaction_type: "Owner Distributions",
      owner: ownerName,
      amount: record.amount || "",
      pay_method: record.pay_method || "cash",
      bank: record.bank || "",
      cheque_no: record.cheque_no || "",
      note: record.note || "",
      pay_method_status: "pay_now",
    });

    setCurrentRecord(record);
    setIsEditing(true);
    setShowModal(true);
  };

  const handleDeleteDistribution = async (id) => {
    const result = await confirmDelete();
    if (!result.isConfirmed) return;

    try {
      await Delete("transactionData", id);

      setDistributions((prevDistributions) =>
        prevDistributions.filter((distribution) => distribution.id !== id)
      );

      handleToast({
        type: "SUCCESS",
        message: "Owner Distribution deleted successfully",
      });

      setRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error("Error deleting Owner Distribution:", error);
      handleToast({
        type: "ERROR",
        message: "Error deleting Owner Distribution: " + error.message,
      });
    }
  };

  return (
    <>
      <ToastContainer />
      <div className="">
        <div>
          <div className="responsive-box">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 style={{ marginLeft: "2px" }}>Owner Distribution</h3>
              <button
                onClick={() => {
                  resetFormData();
                  setShowModal(true);
                }}
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  backgroundColor: "#FFAF32",
                  boxShadow: "none",
                  outline: "none",
                  width: "auto",
                  height: "42px",
                  padding: "8px 12px",
                  fontSize: "14px",
                  border: "none",
                  borderRadius: "10px",
                  color: "#000",
                  marginLeft: "50px",
                  cursor: "pointer",
                  fontWeight: "bold",
                  gap: "8px",
                }}
              >
                Add Owner Distribution
                <img
                  src={OwnerDistributionIcon}
                  alt="Owner Distribution Icon"
                  style={{ width: "20px", height: "20px", marginLeft: "18px" }}
                />
              </button>
            </div>

            <ReusableTable
              fetchData={fetchOwnerDistributions}
              columns={columns}
              showFooter={true}
              showAction={true}
              showExport={true}
              enablePagination={true}
              showFilter={false}
              itemsPerPage={10}
              key={refreshTrigger}
              onEdit={handleEditDistribution}
              onDelete={handleDeleteDistribution}
            />

            {showModal && (
              <div
                style={{
                  position: "fixed",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: "rgba(0,0,0,0.5)",
                  zIndex: 1000,
                }}
                onClick={toggleModal}
              >
                <div
                  onClick={(e) => e.stopPropagation()}
                  style={{
                    width: "700px",
                    backgroundColor: "white",
                    margin: "100px auto",
                    borderRadius: "20px",
                    position: "relative",
                    boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
                  }}
                >
                  <div className="modal-header" style={{ border: "none" }}>
                    <h5
                      className="modal-title p-2"
                      style={{ fontWeight: "bold" }}
                    >
                      {isEditing
                        ? "Edit Owner Distribution"
                        : "Add Owner Distribution"}
                    </h5>

                    <img
                      src={CloseIcon}
                      alt="Close"
                      width="34"
                      height="34"
                      onClick={toggleModal}
                    />
                  </div>
                  <div className="modal-body">
                    <form onSubmit={handleOwnerDistribution}>
                      <div className="form-row d-flex align-items-center ">
                        <div className="form-group col-md-5 position-relative ">
                          <CustomDateInput
                            value={formData && formData.date ? formData.date : today}
                            onChange={handleDateChange}
                            required
                            readOnly
                            inputStyle={{
                              height: "52px",
                              marginLeft: "35px",
                              marginTop: "20px",
                            }}
                          />
                        </div>

                        <div className="form-group col-md-5 ml-3">
                          <select
                            style={{
                              height: "52px",
                              marginLeft: "24%",
                            }}
                            className="input-select"
                            name="owner"
                            value={formData.owner || ""}
                            onChange={(e) => {
                              const selectedOwnerFullName = e.target.value;
                              setFormData((prevState) => ({
                                ...prevState,
                                owner: selectedOwnerFullName,
                              }));
                            }}
                            required
                          >
                            <option value="">Select Owner</option>
                            {ownerList.length > 0 &&
                              ownerList.map((owner) => {
                                const fullName =
                                  `${owner.first_name} ${owner.last_name}`.trim();
                                return (
                                  <option key={owner.id} value={fullName}>
                                    {fullName}
                                  </option>
                                );
                              })}
                          </select>
                        </div>
                      </div>
                      <div className="form-row d-flex col-md-6">
                        <div className="containercolor">
                          <div
                            className="input-group p-2 mt-2"
                            style={{
                              marginBottom: "10px",
                              height: "52px",
                              marginLeft: "27px",
                            }}
                          >
                            <input
                              type="text"
                              name="amount"
                              className="form-control-borderless"
                              value={formData.amount || ""}
                              onChange={handleChange}
                              placeholder="Amount"
                              aria-label="Username"
                              aria-describedby="addon-wrapping"
                              onInput={(e) => {
                                e.target.value = e.target.value
                                  .replace(/[^0-9.]/g, '')
                                  .replace(/^(\d*\.?\d{0,2}).*/g, '$1')
                                  .slice(0, 12);
                              }}
                            />
                          </div>
                        </div>
                      </div>

                      <div
                        className="form-row d-flex col-md-1"
                        style={{
                          border: "1px solid #ACB4AA",
                          padding: "11px 5px",
                          width: "fit-content",
                          borderRadius: "40px",
                          marginTop: "10px",
                          display: "flex",
                          gap: "0px",
                          marginBottom: "20px",
                          marginLeft: "30px",
                        }}
                      >
                        <div className="me-3">
                          <span
                            onClick={() =>
                              handleChange({
                                target: { name: "pay_method", value: "cash" },
                              })
                            }
                            style={{
                              cursor: "pointer",
                              fontWeight:
                                formData.pay_method === "cash"
                                  ? "bold"
                                  : "normal",
                              backgroundColor:
                                formData.pay_method === "cash"
                                  ? "#4a5546"
                                  : "transparent",
                              color:
                                formData.pay_method === "cash"
                                  ? "white"
                                  : "inherit",
                              borderRadius: "20px",
                              padding: "10px 15px",
                            }}
                          >
                            Cash
                          </span>
                        </div>

                        <div>
                          <span
                            onClick={() =>
                              handleChange({
                                target: { name: "pay_method", value: "bank" },
                              })
                            }
                            style={{
                              cursor: "pointer",
                              fontWeight:
                                formData.pay_method === "bank"
                                  ? "bold"
                                  : "normal",
                              backgroundColor:
                                formData.pay_method === "bank"
                                  ? "#4a5546"
                                  : "transparent",
                              color:
                                formData.pay_method === "bank"
                                  ? "white"
                                  : "inherit",
                              borderRadius: "20px",
                              padding: "10px 15px",
                            }}
                          >
                            Bank Card (ACH/EFT)
                          </span>
                        </div>
                        <div className="">
                          <span
                            onClick={() =>
                              handleChange({
                                target: { name: "pay_method", value: "cheque" },
                              })
                            }
                            style={{
                              cursor: "pointer",
                              fontWeight:
                                formData.pay_method === "cheque"
                                  ? "bold"
                                  : "normal",
                              backgroundColor:
                                formData.pay_method === "cheque"
                                  ? "#4a5546"
                                  : "transparent",
                              color:
                                formData.pay_method === "cheque"
                                  ? "white"
                                  : "inherit",
                              borderRadius: "20px",
                              padding: "10px 15px",
                            }}
                          >
                            Cheque
                          </span>
                        </div>
                      </div>

                      <div className="row">
                        {(formData.pay_method === "bank" ||
                          formData.pay_method === "cheque") && (
                            <div className="mb-3 mt-2 col-md-4">
                              <select
                                style={{
                                  marginLeft: "30px",
                                  height: "52px",
                                }}
                                className="input-select"
                                name="bank"
                                value={formData.bank || ""}
                                onChange={handleChange}
                              >
                                <option value="">Select a bank</option>
                                {banks.map((bank) => (
                                  <option key={bank.id} value={bank.id}>
                                    {bank.name}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                        {formData.pay_method === "cheque" && (
                          <div className="mb-3 mt-2 col-md-4">
                            <input
                              className="input-select"
                              style={{
                                marginLeft: "30px",
                                height: "52px",
                              }}
                              type="text"
                              name="cheque_no"
                              value={formData.cheque_no || ""}
                              placeholder="Cheque No"
                              onChange={handleChange}
                                pattern="[0-9]*"
                                  inputMode="numeric"
                            />
                          </div>
                        )}
                      </div>

                      <div className="modal-footer" style={{ border: "none" }}>
                        <button
                          type="button"
                          className="btn btn-secondary-outline"
                          onClick={toggleModal}
                          style={{
                            border: " 1px solid #282e26",
                            borderRadius: "50px",
                            width: "181px",
                            height: "45px",
                            fontWeight: "bold",
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          type="submit"
                          className="btn"
                          style={{
                            color: "white",
                            backgroundColor: "#282e26",
                            borderRadius: "50px",
                            width: "181px",
                            height: "45px",
                            fontWeight: "bold",
                          }}
                        >
                          Submit
                        </button>
                      </div>
                    </form>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}